/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: sms/pages/SmsMessagesPage.tsx
 🗓️ Created: 2026-01-20
 🧩 Description:
    SMS Messages log page - following Extensions module pattern.
────────────────────────────────────────────────────────────────*/

import { useState, useCallback, useMemo, useEffect } from 'react';
import { MessageSquare, Send, RefreshCw, ArrowDownLeft, ArrowUpRight } from 'lucide-react';
import { useI18n } from '../../../hooks/useI18n';
import DataGridSimple from '../../../components/ui/DataGridSimple';
import { useNotifications } from '../../../contexts/NotificationContext';
import { SimpleTooltip } from '../../../components/ui/SimpleTooltip';
import { fetchMessages } from '../api';
import SmsSendModal from '../components/SmsSendModal';
import type { SmsMessage } from '../types';

interface MessageColumn {
    key: string;
    label: string;
    sortable: boolean;
    visible: boolean;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    render?: (value: any, item: SmsMessage) => React.ReactNode;
}

const STATUS_COLORS: Record<string, string> = {
    queued: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
    pending: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
    sent: 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900/30 dark:text-cyan-400',
    delivered: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
    failed: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
    rejected: 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-400',
};

export default function SmsMessagesPage() {
    const { t, i18n } = useI18n();
    const { showError } = useNotifications();
    const [sendModalOpen, setSendModalOpen] = useState(false);
    const [data, setData] = useState<SmsMessage[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [sortBy, setSortBy] = useState('createdAt');
    const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(25);
    const [totalRecords, setTotalRecords] = useState(0);

    const formatDate = (date: string) => new Date(date).toLocaleString();

    // Define columns
    const availableColumns = useMemo<MessageColumn[]>(() => [
        {
            key: 'direction',
            label: '',
            sortable: false,
            visible: true,
            render: (_value: unknown, item: SmsMessage) => (
                item?.direction === 'inbound' ? (
                    <ArrowDownLeft className="h-4 w-4 text-green-500" />
                ) : (
                    <ArrowUpRight className="h-4 w-4 text-blue-500" />
                )
            ),
        },
        {
            key: 'fromNumber',
            label: t('sms.messages.columns.from'),
            sortable: true,
            visible: true,
            render: (_value: unknown, item: SmsMessage) => (
                <span className="font-mono text-sm">{item?.fromNumber || '-'}</span>
            ),
        },
        {
            key: 'toNumber',
            label: t('sms.messages.columns.to'),
            sortable: true,
            visible: true,
            render: (_value: unknown, item: SmsMessage) => (
                <span className="font-mono text-sm">{item?.toNumber || '-'}</span>
            ),
        },
        {
            key: 'body',
            label: t('sms.messages.columns.body'),
            sortable: false,
            visible: true,
            render: (_value: unknown, item: SmsMessage) => (
                <span className="truncate max-w-xs block" title={item?.body}>
                    {item?.body?.substring(0, 50)}{(item?.body?.length || 0) > 50 ? '...' : ''}
                </span>
            ),
        },
        {
            key: 'segments',
            label: t('sms.messages.columns.segments'),
            sortable: true,
            visible: true,
            render: (_value: unknown, item: SmsMessage) => (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-400">
                    {item?.segments || 1}
                </span>
            ),
        },
        {
            key: 'status',
            label: t('sms.messages.columns.status'),
            sortable: true,
            visible: true,
            render: (_value: unknown, item: SmsMessage) => (
                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${STATUS_COLORS[item?.status] || ''}`}>
                    {item?.status || '-'}
                </span>
            ),
        },
        {
            key: 'providerName',
            label: t('sms.messages.columns.provider'),
            sortable: false,
            visible: true,
            render: (_value: unknown, item: SmsMessage) => item?.providerName || '-',
        },
        {
            key: 'createdAt',
            label: t('sms.messages.columns.date'),
            sortable: true,
            visible: true,
            render: (_value: unknown, item: SmsMessage) => formatDate(item?.createdAt),
        },
    ], [t, i18n.language]);

    const [columns, setColumns] = useState<MessageColumn[]>(availableColumns);

    const loadData = useCallback(async () => {
        try {
            setLoading(true);
            const result = await fetchMessages({ page, limit });
            setData(result.messages);
            setTotalRecords(result.pagination.total);
        } catch (error) {
            showError(error.message || t('sms.messages.errorLoading'));
            setData([]);
        } finally {
            setLoading(false);
        }
    }, [page, limit, showError, t]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    // Filter and sort data
    const filteredData = useMemo(() => {
        let result = [...data];

        if (searchQuery) {
            const query = searchQuery.toLowerCase();
            result = result.filter(item =>
                item.fromNumber.toLowerCase().includes(query) ||
                item.toNumber.toLowerCase().includes(query) ||
                item.body.toLowerCase().includes(query)
            );
        }

        result.sort((a, b) => {
            const aVal = (a as any)[sortBy];
            const bVal = (b as any)[sortBy];

            if (sortBy === 'createdAt') {
                const cmp = new Date(aVal).getTime() - new Date(bVal).getTime();
                return sortOrder === 'asc' ? cmp : -cmp;
            }

            if (typeof aVal === 'string') {
                const cmp = aVal.localeCompare(bVal);
                return sortOrder === 'asc' ? cmp : -cmp;
            }

            return sortOrder === 'asc' ? aVal - bVal : bVal - aVal;
        });

        return result;
    }, [data, searchQuery, sortBy, sortOrder]);

    const pagination = useMemo(() => ({
        page,
        limit,
        total: totalRecords,
        totalPages: Math.ceil(totalRecords / limit),
    }), [page, limit, totalRecords]);

    const handleSearch = useCallback((query: string) => {
        setSearchQuery(query);
        setPage(1);
    }, []);

    const handleSort = useCallback((key: string) => {
        if (sortBy === key) {
            setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
        } else {
            setSortBy(key);
            setSortOrder('desc');
        }
    }, [sortBy]);

    const handlePageChange = useCallback((newPage: number) => {
        setPage(newPage);
    }, []);

    const handleItemsPerPageChange = useCallback((newLimit: number) => {
        setLimit(newLimit);
        setPage(1);
    }, []);

    const handleColumnToggle = useCallback((key: string) => {
        setColumns(prev =>
            prev.map(col =>
                col.key === key ? { ...col, visible: !col.visible } : col
            )
        );
    }, []);

    const handleSendClose = useCallback((refresh?: boolean) => {
        setSendModalOpen(false);
        if (refresh) {
            loadData();
        }
    }, [loadData]);

    return (
        <div className="p-6">
            <DataGridSimple
                data={filteredData}
                loading={loading}
                columns={columns}
                pagination={pagination}
                searchQuery={searchQuery}
                sortBy={sortBy}
                sortOrder={sortOrder}
                onSearch={handleSearch}
                onSort={handleSort}
                onPageChange={handlePageChange}
                onItemsPerPageChange={handleItemsPerPageChange}
                onColumnToggle={handleColumnToggle}
                searchPlaceholder={t('sms.messages.searchPlaceholder')}
                title={t('sms.messages.title')}
                titleIcon={<MessageSquare className="h-5 w-5 text-primary" />}
                actions={
                    <div className="flex items-center gap-2">
                        <SimpleTooltip content={t('common.refresh')} position="top">
                            <button
                                onClick={loadData}
                                disabled={loading}
                                className="flex items-center gap-2 px-3 py-2 bg-gray-100 dark:bg-gray-800 text-foreground rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors disabled:opacity-50"
                            >
                                <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                                <span className="hidden sm:inline">{t('common.refresh')}</span>
                            </button>
                        </SimpleTooltip>
                        <SimpleTooltip content={t('sms.messages.actions.send')} position="top">
                            <button
                                onClick={() => setSendModalOpen(true)}
                                className="flex items-center gap-1.5 px-3 py-1.5 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors text-sm"
                            >
                                <Send className="h-4 w-4" />
                                <span className="hidden sm:inline">{t('sms.messages.actions.send')}</span>
                            </button>
                        </SimpleTooltip>
                    </div>
                }
            />

            <SmsSendModal
                isOpen={sendModalOpen}
                onClose={handleSendClose}
            />
        </div>
    );
}
